// Input Area Component — ChatGPT-style "+" attach + right-side mic/voice mode
import { useRef, useState, useEffect, memo } from "react";
import {
  Send, X, Sparkles, Upload, Loader2,
  Mic, ImagePlus, Camera, FileText, Plus, Wand2, AudioLines,
  Zap, Brain, Gauge, Video, ScanFace
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { FaceSelectModal } from "@/components/modals/FaceSelectModal";
import { useLanguage } from "@/i18n";

function InputAreaComponent({
  mode,
  setMode,
  inputValue,
  setInputValue,
  imagePrompt,
  setImagePrompt,
  imagePreview,
  uploadedImage,
  cancelImageUpload,
  isRecording,
  isTranscribing,
  startRecording,
  stopRecording,
  isLoading,
  isAnalyzingImage,
  sendMessage,
  generateImage,
  analyzeImage,
  editUploadedImage,
  isEditingImage,
  selectedFaces,
  setSelectedFaces,
  onOpenVideoStudio,
  handleKeyPress,
  handleImageSelect,
  textareaRef,
  imageInputRef,
  // Voice mode (ChatGPT-style live conversation)
  openVoiceMode,
  // Live mic level (0..1) during recording — drives visualizer
  micLevel = 0,
  micBands = [],
  // File mode
  selectedFile,
  setSelectedFile,
  fileQuestion,
  setFileQuestion,
  analyzeFile,
  fileInputRef
}) {
  const { t } = useLanguage();
  const cameraInputRef = useRef(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [faceModalOpen, setFaceModalOpen] = useState(false);

  // Chat-screen model mode selector (Speed/Quality). Reads & writes the SAME
  // localStorage key ("kaan-ai-settings".modelMode) the settings panel uses;
  // App.js reads this value at send time, so no extra wiring is needed.
  const [modelOpen, setModelOpen] = useState(false);
  const [modelMode, setModelMode] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kaan-ai-settings") || "{}").modelMode || "auto"; }
    catch { return "auto"; }
  });
  const MODEL_OPTIONS = [
    { id: "fast", icon: Zap, labelKey: "model_fast", descKey: "model_fast_desc", color: "text-yellow-400" },
    { id: "auto", icon: Sparkles, labelKey: "model_auto", descKey: "model_auto_desc", color: "text-blue-400" },
    { id: "pro", icon: Brain, labelKey: "model_pro", descKey: "model_pro_desc", color: "text-purple-400" },
  ];
  const changeModelMode = (id) => {
    setModelMode(id);
    setModelOpen(false);
    try {
      const s = JSON.parse(localStorage.getItem("kaan-ai-settings") || "{}");
      s.modelMode = id;
      localStorage.setItem("kaan-ai-settings", JSON.stringify(s));
    } catch { /* ignore */ }
  };
  const activeModel = MODEL_OPTIONS.find((m) => m.id === modelMode) || MODEL_OPTIONS[1];

  // Recording duration timer (mm:ss)
  useEffect(() => {
    if (!isRecording) { setRecordSecs(0); return; }
    setRecordSecs(0);
    const started = Date.now();
    const id = setInterval(() => setRecordSecs(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  const closeAttach = () => setAttachOpen(false);
  const timeLabel = `${String(Math.floor(recordSecs / 60)).padStart(2, "0")}:${String(recordSecs % 60).padStart(2, "0")}`;

  // Video generation is hidden until the provider is reliable. Flip to true to re-enable.
  const VIDEO_ENABLED = false;

  const menuItems = [
    {
      key: "photo",
      testid: "attach-photo",
      icon: <ImagePlus size={18} />,
      label: t("attach_photo"),
      color: "text-emerald-400",
      onClick: () => { closeAttach(); imageInputRef.current?.click(); },
    },
    {
      key: "generate",
      testid: "attach-generate",
      icon: <Wand2 size={18} />,
      label: t("attach_generate"),
      color: "text-pink-400",
      onClick: () => { closeAttach(); setMode("image"); },
    },
    {
      key: "video",
      testid: "attach-video",
      icon: <Video size={18} />,
      label: t("attach_video"),
      color: "text-purple-400",
      onClick: () => { closeAttach(); onOpenVideoStudio && onOpenVideoStudio(); },
    },
    {
      key: "camera",
      testid: "attach-camera",
      icon: <Camera size={18} />,
      label: t("attach_camera"),
      color: "text-sky-400",
      onClick: () => { closeAttach(); cameraInputRef.current?.click(); },
    },
    {
      key: "file",
      testid: "attach-file",
      icon: <FileText size={18} />,
      label: t("attach_file"),
      color: "text-amber-400",
      onClick: () => { closeAttach(); setMode("file"); setTimeout(() => fileInputRef.current?.click(), 60); },
    },
  ].filter((m) => VIDEO_ENABLED || m.key !== "video");

  return (
    <div className="input-area">
      <div className="input-container">
        {imagePreview && (
          <div className="mb-3 relative">
            <div className="flex items-start gap-3 bg-[var(--bg-tertiary)] p-3 rounded-lg">
              <img src={imagePreview} alt={t('image_ready')} className="w-20 h-20 object-cover rounded-lg shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--text-secondary)]">{t('image_ready')}</p>
                <p className="text-xs text-[var(--text-muted)] truncate">{uploadedImage?.name}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    onClick={analyzeImage}
                    disabled={isAnalyzingImage || isEditingImage}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"
                    data-testid="analyze-image-action"
                  >
                    {isAnalyzingImage ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />}
                    {t('img_analyze_action')}
                  </Button>
                  <Button
                    onClick={editUploadedImage}
                    disabled={(!inputValue.trim() && !selectedFaces.some((f) => (f.instruction || '').trim())) || isEditingImage || isAnalyzingImage}
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-500 text-white h-8 disabled:opacity-50"
                    data-testid="edit-image-action"
                  >
                    {isEditingImage ? <Loader2 size={14} className="animate-spin mr-1" /> : <Wand2 size={14} className="mr-1" />}
                    {t('img_edit_action')}
                  </Button>
                  <Button
                    onClick={() => setFaceModalOpen(true)}
                    disabled={isEditingImage || isAnalyzingImage}
                    size="sm"
                    variant="outline"
                    className="h-8 border-[var(--border-color)] text-[var(--text-secondary)]"
                    data-testid="select-face-action"
                  >
                    <ScanFace size={14} className="mr-1" />
                    {selectedFaces.length === 1
                      ? selectedFaces[0].label
                      : selectedFaces.length > 1
                        ? t('face_n_selected').replace('{n}', selectedFaces.length)
                        : t('face_select_btn')}
                  </Button>
                  {selectedFaces.length > 0 && (
                    <button
                      onClick={() => setSelectedFaces([])}
                      data-testid="clear-face-selection"
                      className="h-8 px-2 text-xs text-[var(--text-muted)] hover:text-red-400 flex items-center gap-1"
                    >
                      <X size={12} /> {t('face_clear')}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-[var(--text-muted)] mt-1.5 leading-snug">
                  {selectedFaces.some((f) => (f.instruction || '').trim())
                    ? t('face_per_person_applied')
                    : selectedFaces.length > 0
                      ? t('face_scoped_hint').replace('{who}', selectedFaces.length === 1
                          ? selectedFaces[0].label
                          : t('face_n_people').replace('{n}', selectedFaces.length))
                      : t('img_action_hint')}
                </p>
              </div>
              <button
                onClick={cancelImageUpload}
                data-testid="cancel-image-btn"
                className="text-[var(--text-muted)] hover:text-red-400 transition-colors shrink-0"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        )}

        <FaceSelectModal
          open={faceModalOpen}
          onOpenChange={setFaceModalOpen}
          imageSrc={imagePreview}
          selectedFaces={selectedFaces}
          onApply={(faces) => setSelectedFaces(faces)}
        />

        <input
          type="file"
          ref={imageInputRef}
          onChange={handleImageSelect}
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          data-testid="image-upload-input"
        />
        <input
          type="file"
          ref={cameraInputRef}
          onChange={handleImageSelect}
          accept="image/*"
          capture="environment"
          className="hidden"
          data-testid="camera-capture-input"
        />

        {(mode === "chat" || mode === "image_analysis") && (
          <div className={`input-wrapper ${isRecording ? "is-recording recording-mode" : ""}`}>
            {isRecording ? (
              <>
                <button
                  className="rec-stop"
                  onClick={stopRecording}
                  data-testid="stop-recording-btn"
                  title={t('stop_recording')}
                  aria-label={t('stop_recording')}
                >
                  <span className="rec-stop-square" />
                </button>

                <div className="rec-timer" data-testid="record-timer">
                  <span className="rec-timer-dot" />
                  <span className="rec-timer-text">{timeLabel}</span>
                </div>

                {/* Live equalizer — 12 gradient bars dancing with real voice frequency bands */}
                {(() => {
                  const bands = (micBands && micBands.length ? micBands : new Array(12).fill(0));
                  const anySignal = bands.some((v) => v > 0.06) || micLevel > 0.06;
                  return (
                    <div
                      className={`voice-eq voice-eq-rec ${anySignal ? "voice-eq-active" : ""}`}
                      data-testid="recording-indicator"
                      aria-label={anySignal ? t('voice_detected') : t('voice_awaiting')}
                      title={anySignal ? t('voice_detected') : t('voice_awaiting')}
                    >
                      {bands.map((v, i) => {
                        const h = Math.max(4, Math.round(6 + v * 34));
                        return (
                          <span
                            key={i}
                            className="voice-eq-bar"
                            style={{ height: `${h}px` }}
                          />
                        );
                      })}
                    </div>
                  );
                })()}

                <button
                  type="button"
                  onClick={stopRecording}
                  data-testid="cancel-recording-btn"
                  className="rec-cancel"
                  title={t('cancel')}
                  aria-label={t('cancel')}
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                {/* "+" Attach popover — ChatGPT-style, LEFT of textarea */}
                <Popover open={attachOpen} onOpenChange={setAttachOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="attach-plus-btn"
                      title={t('attach')}
                      className="attach-plus-btn"
                      aria-label={t('attach')}
                    >
                      <Plus
                        size={20}
                        className={`transition-transform duration-200 ${attachOpen ? "rotate-45" : ""}`}
                      />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="top"
                    sideOffset={10}
                    className="w-60 p-1.5 bg-zinc-900 border-zinc-700 shadow-2xl rounded-2xl"
                    data-testid="attach-menu"
                  >
                    {menuItems.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        data-testid={item.testid}
                        onClick={item.onClick}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-zinc-200 hover:bg-zinc-800 transition-colors"
                      >
                        <span className={`${item.color}`}>{item.icon}</span>
                        <span className="flex-1">{item.label}</span>
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Model mode selector — Speed / Quality, next to attach */}
                <Popover open={modelOpen} onOpenChange={setModelOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="model-mode-btn"
                      title={t('ai_model_mode')}
                      aria-label={t('ai_model_mode')}
                      className="flex items-center gap-1.5 px-2.5 h-9 rounded-full border border-zinc-700 bg-zinc-800/60 text-zinc-200 hover:border-blue-500 hover:text-blue-300 transition-colors text-xs font-medium"
                    >
                      <activeModel.icon size={15} className={activeModel.color} />
                      <span className="hidden sm:inline">{t(activeModel.labelKey)}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side="top"
                    sideOffset={10}
                    className="w-64 p-1.5 bg-zinc-900 border-zinc-700 shadow-2xl rounded-2xl"
                    data-testid="model-mode-menu"
                  >
                    <div className="px-3 py-1.5 text-[11px] text-zinc-500">{t('ai_model_mode')}</div>
                    {MODEL_OPTIONS.map((m) => {
                      const Icon = m.icon;
                      const selected = modelMode === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          data-testid={`model-mode-${m.id}`}
                          onClick={() => changeModelMode(m.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                            selected ? "bg-blue-600/20" : "hover:bg-zinc-800"
                          }`}
                        >
                          <Icon size={18} className={m.color} />
                          <span className="flex-1">
                            <span className="block text-sm text-zinc-100">{t(m.labelKey)}</span>
                            <span className="block text-[11px] text-zinc-500">{t(m.descKey)}</span>
                          </span>
                          {selected && <span className="w-2 h-2 rounded-full bg-blue-400" />}
                        </button>
                      );
                    })}
                  </PopoverContent>
                </Popover>

                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={imagePreview ? t('img_prompt_placeholder') : t('type_message')}
                  rows={1}
                  disabled={isLoading || isAnalyzingImage || isEditingImage}
                  data-testid="chat-input"
                />

                {/* Right cluster: voice-mode + (mic when empty | send when typing).
                    Hidden while an image is staged — actions live in the preview card. */}
                {!imagePreview && openVoiceMode && (
                  <button
                    type="button"
                    className="icon-btn voice-mode-button"
                    onClick={openVoiceMode}
                    data-testid="voice-mode-btn"
                    title={t('voice_mode')}
                    aria-label={t('voice_mode')}
                  >
                    <AudioLines size={18} />
                  </button>
                )}

                {imagePreview ? null : inputValue.trim() ? (
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={isLoading}
                    className="send-btn"
                    data-testid="send-button"
                    title={t('send')}
                    aria-label={t('send')}
                  >
                    {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    data-testid="mic-button"
                    disabled={isTranscribing}
                    className="icon-btn mic-button"
                    title={isTranscribing ? t('processing') : t('voice_command')}
                    aria-label={t('voice_command')}
                  >
                    {isTranscribing ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {mode === "image" && (
          <div className="space-y-3">
            <div className="input-wrapper">
              <Sparkles size={20} className="text-blue-500 ml-3" />
              <textarea
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('describe_image')}
                rows={1}
                disabled={isLoading}
                className="single-line-input"
                data-testid="image-input"
              />
              <Button
                onClick={generateImage}
                disabled={!imagePrompt.trim() || isLoading}
                className="bg-blue-600 hover:bg-blue-700 rounded-xl px-4"
                data-testid="generate-image-button"
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
              </Button>
            </div>
          </div>
        )}

        {mode === "file" && (
          <div className="input-wrapper">
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              accept=".txt,.pdf,.doc,.docx,.csv,.json,.md,.py,.js,.html,.css"
              className="hidden"
              data-testid="file-input"
            />
            <Button
              variant="ghost"
              onClick={() => fileInputRef.current?.click()}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              data-testid="file-choose-btn"
            >
              <Upload size={20} />
              {selectedFile ? selectedFile.name : t('select_file')}
            </Button>
            <textarea
              value={fileQuestion}
              onChange={(e) => setFileQuestion(e.target.value)}
              placeholder={t('ask_about_file')}
              rows={1}
              disabled={isLoading}
              className="single-line-input"
              data-testid="file-question-input"
            />
            <Button
              onClick={analyzeFile}
              disabled={!selectedFile || isLoading}
              className="bg-blue-600 hover:bg-blue-700 rounded-xl px-4"
              data-testid="analyze-file-btn"
            >
              {isLoading ? <Loader2 size={18} className="animate-spin" /> : <FileText size={18} />}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export const InputArea = memo(InputAreaComponent);
export default InputArea;
